import React, { useCallback, useState, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Price, Loader, ProductGallery, ProductFeature, RoutedLink } from '@/components';
import Skeleton from 'react-loading-skeleton';
import numberWithSpaces from '@/lib/numberWithSpaces';
import { marketplaceFeatures } from '@/lib/config.js';
import useLocalStorage from '@/hooks/use-local-storage';
import { getCorrectName } from '@/utils';
import Icon from '@/assets/icons';
import { BreadCrumbs } from '@/containers';
import { useNotification } from '@/contexts/NotificationContext';
import dataLayer from '@/seo/dataLayer/DataLayer';

import clsx from 'clsx';
import styles from './Product.module.scss';

const EXCLUDED_INFO_ATTR_SLUGS = ['strana-dizayna', 'strana-proizvoditel'];

const ProductShop = dynamic(() => import('../../components/productShop'), {
	loading: () => <Loader />,
});

const ProductConfig = dynamic(() => import('../../components/productConfig'), {
	loading: () => <Loader />,
});

const Accordion = dynamic(() => import('../../components/accordion'), {
	loading: () => <Loader />,
});

const BrandLink = ({ brand, link }) => {
	const words = brand.split(' ');
	const lastWord = words[words.length - 1];
	const otherWords = words.slice(0, -1).join(' ');

	return (
		<RoutedLink to={`/${link}`}>
			{otherWords}{' '}
			<span className={styles.withArrow}>
				{lastWord} <Icon name='arrow-up' />
			</span>
		</RoutedLink>
	);
};

const Product = ({ data, productInfo, dlItemListId, dlItemListName }) => {
	const {
		attributes = [],
		brand,
		brand_url,
		breadcrumbs,
		name,
		price,
		old_price,
		price_diff_percent,
		quantity,
		gallery,
		gallery_thumbs,
		gallery_zoom,
		images_info,
		features,
		shops,
		sku,
		description: descriptionRaw = '',
		importer,
		labels = {},
		seo = {},
		switchable_attributes,
		clickOpenShops,
		show_shops = true,
		no_delivery_to_russia = false,
		loadingShops,
		model,
		sex = null,
	} = data;
	const { id: modelId, slug: modelSlug } = model;
	const [currency, _setCurrency] = useLocalStorage('currency', 'BY');
	const inStock = quantity !== 0;
	const [actualPrice, setActualPrice] = useState(price);
	const [activeImageName, setActiveImageName] = useState(null);
	const [colorState, setColorState] = useState(null);
	const notification = useNotification();

	const description = useMemo(() => {
		return descriptionRaw && descriptionRaw?.replaceAll
			? descriptionRaw.replaceAll('\r', '\n\r')
			: descriptionRaw;
	}, [descriptionRaw]);

	useEffect(() => {
		setActualPrice(price);
	}, [price, modelSlug]);

	const priceBYN = useMemo(() => {
		if (currency == 'RU') {
			const hasDiscount = Number(old_price) > Number(actualPrice);

			return (
				<div className={styles.bynPriceBlock}>
					<div className={clsx(styles.bynPrice, hasDiscount && styles.bynPriceOld)}>
						{numberWithSpaces(hasDiscount ? old_price : actualPrice)} BYN
					</div>
					{hasDiscount && (
						<div className={clsx(styles.bynPrice, styles.bynPriceSale)}>
							{numberWithSpaces(actualPrice)} BYN
						</div>
					)}
				</div>
			);
		}
		return null;
	}, [currency, actualPrice, old_price]);

	const infoAttributes = useMemo(
		() =>
			attributes.filter(
				attr =>
					attr.type === 'info' &&
					attr.value !== '' &&
					!EXCLUDED_INFO_ATTR_SLUGS.includes(attr.slug),
			),
		[attributes],
	);

	const hasImporterBlock =
		importer?.name ||
		importer?.address ||
		importer?.manufacturer_name ||
		importer?.manufacturer_address ||
		importer?.country_of_design ||
		importer?.country_of_origin;

	const switchAttributes = useMemo(() => attributes.filter(attr => attr.type === 'switch'), [
		attributes,
	]);

	const [currentSlug, setCurrentSlug] = useState(modelSlug);
	const [open, setOpen] = useState(false);

	const handleSetCurrentSlug = useCallback(newSlug => {
		setCurrentSlug(newSlug);
	}, []);

	const alt = useMemo(() => {
		return `${sex ? `${sex} ` : ''}${brand} ${name} (цвет ${
			activeImageName ? activeImageName?.toLowerCase() : ''
		}), артикул ${sku}`;
	}, [brand, sku, name, switchable_attributes, activeImageName]);

	const handleClickOpenShops = () => {
		setOpen(true);
		clickOpenShops();

		dataLayer.viewStoreAvailability({
			item_list_id: productInfo?.listId ?? dlItemListId,
			item_list_name: productInfo?.listName ?? dlItemListName,
			price: data.price,
			items: [{ ...data }],
		});
	};

	useEffect(() => {
		setOpen(false);
		setCurrentSlug(modelSlug);
	}, [modelSlug]);

	const handleSetActualPrice = useCallback(
		newPrice => {
			setActualPrice(newPrice ?? price);
		},
		[price],
	);

	const handleColorState = useCallback(
		value => {
			if (colorState !== value) {
				setColorState(value);
			}
		},
		[colorState],
	);

	const onLoadingError = event => {
		event.target?.classList.add(styles.loadingError);
	};

	const copyText = async sku => {
		await navigator.clipboard.writeText(sku);

		notification.add({
			id: 'copy-sku',
			style: 'warning',
			content: <p>Артикул скопирован</p>,
			duration: 2000,
			closable: true,
		});
	};

	return (
		<div className={styles.product}>
			<div className={styles.breadCrumbs}>
				<BreadCrumbs breadcrumbs={breadcrumbs} />
			</div>
			<div className={clsx(styles.gallery, 'productGallery')}>
				<ProductGallery
					key={modelId}
					images={gallery}
					thumbs={gallery_thumbs}
					zoom={gallery_zoom}
					alt={alt}
					title={images_info?.title ?? null}
					onLoadingError={onLoadingError}
				/>
			</div>

			<div>
				<div className={styles.header}>
					<h1 className={styles.title}>
						{brand && (
							<span className={styles.brand}>
								{brand_url ? <BrandLink brand={brand} link={brand_url} /> : brand}
							</span>
						)}
						<span className={styles.desc}>{seo && seo.h1 ? getCorrectName(seo.h1) : name}</span>
						{sku && (
							<p className={clsx(styles.attributes, styles.sku)}>
								<span className={styles.name}>Артикул товара:</span>
								<span
									className={styles.value}
									onClick={() => {
										copyText(sku);
									}}
									title='Копировать артикул'
								>
									{sku}
								</span>
							</p>
						)}
					</h1>

					{features && features.length > 0 && (
						<ul className={styles.features}>
							{features
								// .sort((a, b) => (Number(a.priority) > Number(b.priority) ? -1 : 1))
								.map(({ id, label, type, text, is_product_card }, ind) =>
									is_product_card ? (
										<li className={styles.feature} key={id || ind}>
											<ProductFeature title={label} styledType={type} text={text} />
										</li>
									) : null,
								)}
						</ul>
					)}
				</div>

				<div className={styles.content}>
					<ProductConfig
						modelId={modelId}
						switchAttributes={switchAttributes}
						slug={modelSlug}
						actualPrice={price}
						currentSlug={currentSlug}
						setCurrentSlug={handleSetCurrentSlug}
						inStock={inStock}
						data={data}
						productInfo={productInfo}
						isProductPage
						setActualPrice={handleSetActualPrice}
						setColorState={handleColorState}
						setActiveImageName={setActiveImageName}
						onLoadingError={onLoadingError}
					>
						<>
							{inStock && (
								<>
									<div
										className={clsx(
											{
												[styles.singleCell]: !(
													Number(old_price) > Number(actualPrice) && !!price_diff_percent
												),
											},
											styles.priceWrap,
											currency == 'BY' && styles.priceWrapBY,
										)}
									>
										{Number(old_price) > Number(actualPrice) && !!price_diff_percent && (
											<div className={styles.discountPercent}>
												<ProductFeature title={`${price_diff_percent}%`} styledType='sale' />
											</div>
										)}

										<div className={styles.prices}>
											<Price price={actualPrice} oldPrice={old_price} isProductCard isProduct />
										</div>
									</div>
									<div>{priceBYN}</div>
								</>
							)}
							{labels?.counter_message && (
								<div className={styles.buy}>{labels.counter_message}</div>
							)}
						</>
					</ProductConfig>

					<ul className={styles.marketplaceFeatures}>
						{marketplaceFeatures.map(feature => {
							const { icon, text } = feature;
							return (
								<li className={styles.item} key={text}>
									<Icon name={icon} />
									{icon === 'delivery' && no_delivery_to_russia
										? 'Быстрая доставка по Беларуси'
										: text}
								</li>
							);
						})}
					</ul>

					<div className={styles.accordions}>
						{(description || sku || infoAttributes.length > 0) && (
							<Accordion
								headChild={<h4 className={styles.accordionHeader}>Описание</h4>}
								mod='product'
								initialOpen
							>
								{description && <p className={styles.description}>{description}</p>}

								{infoAttributes.map(({ name: infoName, value }) => (
									<p className={clsx(styles.attributes, styles.attrDesc)} key={infoName}>
										<span className={styles.name}>{infoName}:</span>{' '}
										<span className={styles.value}>{value}</span>
									</p>
								))}
							</Accordion>
						)}

						{hasImporterBlock && (
							<Accordion
								headChild={<h4 className={styles.accordionHeader}>Импортер</h4>}
								mod='product'
							>
								{importer?.name && <p>Импортер: {importer.name}</p>}
								{importer?.address && <p>Адрес: {importer.address}</p>}
								{importer?.manufacturer_name && (
									<p className={styles.importerLine}>Производитель: {importer.manufacturer_name}</p>
								)}
								{importer.manufacturer_address && (
									<p>
										Адрес: {importer.country_of_design ? `${importer.country_of_design}, ` : ''}
										{importer.manufacturer_address}
									</p>
								)}
								{importer?.country_of_origin && (
									<p>Страна происхождения товара: {importer.country_of_origin}</p>
								)}
							</Accordion>
						)}

						{show_shops && (
							<Accordion
								headChild={<h4 className={styles.accordionHeader}>Наличие в магазинах</h4>}
								mod='product'
								clickOpen={handleClickOpenShops}
								unmountOnClose
								initialOpen={open}
							>
								{loadingShops ? (
									<>
										<Skeleton
											key='skeleton1'
											count={1}
											height={23}
											width='90%'
											style={{ marginBottom: '16px' }}
										/>
										<Skeleton
											key='skeleton2'
											count={1}
											height={20}
											width='40%'
											style={{ marginBottom: '8px' }}
										/>
										<Skeleton
											key='skeleton3'
											count={1}
											height={20}
											width='80%'
											style={{ marginBottom: '8px' }}
										/>
										<Skeleton
											key='skeleton4'
											count={1}
											height={20}
											width='60%'
											style={{ marginBottom: '16px' }}
										/>
										<Skeleton
											key='skeleton5'
											count={1}
											height={20}
											width='40%'
											style={{ marginBottom: '8px' }}
										/>
										<Skeleton
											key='skeleton6'
											count={1}
											height={23}
											width='90%'
											style={{ marginBottom: '16px' }}
										/>
									</>
								) : (
									shops &&
									shops.map(shop => (
										<ProductShop
											key={shop.name}
											{...shop}
											activeVariantId={modelSlug}
											colorState={colorState}
										/>
									))
								)}
							</Accordion>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Product;
